import { memo, useCallback, useMemo, useState } from 'react';
import classNames from 'classnames';
import { MdChevronLeft, MdClose } from 'react-icons/md';
import {
  V2QsSelectSheetProps,
  V2QsSelectSheetOption,
  V2QsSelectSheetValue,
  defaultV2QsSelectSheetProps,
} from './types';

/**
 * V2QsSelectSheet 컴포넌트
 *
 * 바텀시트 모달을 통한 선택 컴포넌트
 * V1 QsInputSelectSheet의 V2 버전으로 순수 UI만 담당
 *
 * 주요 기능:
 * - 바텀시트 모달로 옵션 선택
 * - 단일/다중 선택 지원
 * - requireNote 옵션 지원 (선택 시 추가 노트 입력)
 * - mutualExclusive 옵션 지원
 */
function V2QsSelectSheet(props: V2QsSelectSheetProps) {
  const {
    options,
    value,
    onChange,
    single = defaultV2QsSelectSheetProps.single,
    isOpen: controlledIsOpen,
    onOpen,
    onClose,
    disabled = defaultV2QsSelectSheetProps.disabled,
    readOnly = defaultV2QsSelectSheetProps.readOnly,
    hasError = defaultV2QsSelectSheetProps.hasError,
    errorMessage,
    isRtl = defaultV2QsSelectSheetProps.isRtl,
    title,
    placeholder = defaultV2QsSelectSheetProps.placeholder,
    closeButtonText = defaultV2QsSelectSheetProps.closeButtonText,
    notePlaceholder = defaultV2QsSelectSheetProps.notePlaceholder,
    noteRequiredMessage = defaultV2QsSelectSheetProps.noteRequiredMessage,
    className,
    inputClassName,
    modalClassName,
  } = props;

  // 내부 상태 (외부 제어가 없을 때 사용)
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const [markError, setMarkError] = useState(false);

  // 외부 제어 또는 내부 상태 사용
  const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;

  // 현재 값
  const selectedKeys = value?.selectedKeys || [];
  const notesForKeys = value?.notesForKeys || {};

  // 첫 번째 선택된 옵션
  const firstSelectedKey = selectedKeys[0];
  const firstSelectedOption = options.find((opt) => opt.key === firstSelectedKey);
  const firstSelectedLabel = firstSelectedOption?.label || '';

  // mutualExclusive 옵션 키 목록
  const mutualExclusiveKeys = useMemo(() => {
    return options.filter((opt) => opt.mutualExclusive).map((opt) => opt.key);
  }, [options]);

  // 노트 필수인데 비어있는 키 목록
  const keysWithInvalidNote = useMemo(() => {
    return selectedKeys.filter((key) => {
      const option = options.find((opt) => opt.key === key);
      if (!option?.requireNote) return false;
      const note = notesForKeys[key] || '';
      return note.trim() === '';
    });
  }, [selectedKeys, options, notesForKeys]);

  // 모달 열기
  const handleOpen = useCallback(() => {
    if (disabled || readOnly) return;
    setMarkError(false);
    if (onOpen) {
      onOpen();
    } else {
      setInternalIsOpen(true);
    }
  }, [disabled, readOnly, onOpen]);

  // 모달 닫기
  const handleClose = useCallback(() => {
    // 노트 필수 검증
    if (keysWithInvalidNote.length > 0) {
      setMarkError(true);
      return;
    }

    if (onClose) {
      onClose();
    } else {
      setInternalIsOpen(false);
    }
  }, [keysWithInvalidNote, onClose]);

  // 옵션 선택 처리
  const handleSelectOption = useCallback(
    (optionKey: string, checked: boolean) => {
      if (!onChange) return;

      const option = options.find((opt) => opt.key === optionKey);
      const isMutualExclusive = option?.mutualExclusive || false;

      let newSelectedKeys: string[];

      if (checked) {
        if (isMutualExclusive) {
          // 상호 배타적 옵션 선택 시 다른 옵션 모두 해제
          newSelectedKeys = [optionKey];
        } else {
          // 일반 옵션 선택 시 상호 배타적 옵션 해제
          newSelectedKeys = [...selectedKeys.filter((k) => !mutualExclusiveKeys.includes(k)), optionKey];
        }
      } else {
        newSelectedKeys = selectedKeys.filter((k) => k !== optionKey);
      }

      // 단일 선택 모드일 때 마지막 선택만 유지
      if (single && newSelectedKeys.length > 1) {
        newSelectedKeys = [newSelectedKeys[newSelectedKeys.length - 1]];
      }

      // 제거된 키의 노트 삭제
      const newNotesForKeys: Record<string, string> = {};
      newSelectedKeys.forEach((key) => {
        if (notesForKeys[key]) {
          newNotesForKeys[key] = notesForKeys[key];
        }
      });

      onChange({
        selectedKeys: newSelectedKeys,
        notesForKeys: newNotesForKeys,
      });

      // 단일 선택이고 노트 필수가 아니면 자동 닫기
      if (single && checked && !option?.requireNote) {
        if (onClose) {
          onClose();
        } else {
          setInternalIsOpen(false);
        }
      }
    },
    [onChange, options, selectedKeys, mutualExclusiveKeys, single, notesForKeys, onClose]
  );

  // 노트 업데이트
  const handleNoteChange = useCallback(
    (optionKey: string, note: string) => {
      if (!onChange) return;

      onChange({
        selectedKeys,
        notesForKeys: {
          ...notesForKeys,
          [optionKey]: note,
        },
      });
    },
    [onChange, selectedKeys, notesForKeys]
  );

  // 옵션 렌더링 (모달 내부)
  const renderOption = (option: V2QsSelectSheetOption) => {
    const isSelected = selectedKeys.includes(option.key);
    const showNoteInput = isSelected && option.requireNote;
    const noteValue = notesForKeys[option.key] || '';
    const noteInvalid = markError && option.requireNote && noteValue.trim() === '';

    return (
      <div key={option.key}>
        <label
          className={classNames(
            'cursor-pointer border border-gray-300 p-3 rounded-lg border-opacity-20',
            'flex flex-col gap-3 hover:border-gray-400 transition-colors',
            option.disabled && 'opacity-50 cursor-not-allowed'
          )}
        >
          <div className="flex-1 w-full flex flex-row gap-4 items-center">
            <input
              type={single ? 'radio' : 'checkbox'}
              checked={isSelected}
              disabled={option.disabled}
              onChange={(e) => handleSelectOption(option.key, e.target.checked)}
              className={classNames(
                single
                  ? 'radio radio-lg radio-primary'
                  : 'checkbox checkbox-lg checkbox-primary'
              )}
            />
            <span className="pt-[0.2rem]">{option.label}</span>
          </div>

          {showNoteInput && (
            <div className="flex flex-col">
              <input
                type="text"
                value={noteValue}
                onChange={(e) => handleNoteChange(option.key, e.target.value)}
                placeholder={notePlaceholder}
                className={classNames(
                  'input w-full border border-black border-opacity-10 rounded-xl',
                  noteInvalid && 'border-error'
                )}
              />
            </div>
          )}
        </label>
      </div>
    );
  };

  // 선택된 항목 렌더링 (모달 외부, 다중 선택 시)
  const renderSelectedItem = (optionKey: string) => {
    const option = options.find((opt) => opt.key === optionKey);
    if (!option) return null;

    const showNoteInput = option.requireNote;
    const noteValue = notesForKeys[optionKey] || '';
    const noteInvalid = markError && option.requireNote && noteValue.trim() === '';

    return (
      <div key={optionKey}>
        <label
          className={classNames(
            'cursor-pointer border border-black p-3 rounded-xl border-opacity-10',
            'flex flex-col gap-3'
          )}
        >
          <div className="flex-1 w-full flex flex-row gap-4 items-center">
            <input
              type={single ? 'radio' : 'checkbox'}
              checked
              onChange={(e) => handleSelectOption(optionKey, e.target.checked)}
              className={classNames(
                single
                  ? 'radio radio-lg radio-primary'
                  : 'checkbox checkbox-lg checkbox-primary'
              )}
            />
            <span className="pt-[0.2rem]">{option.label}</span>
          </div>

          {showNoteInput && (
            <div className="flex flex-col">
              <input
                type="text"
                value={noteValue}
                onChange={(e) => handleNoteChange(optionKey, e.target.value)}
                placeholder={notePlaceholder}
                className={classNames(
                  'input w-full border border-black border-opacity-10 rounded-xl',
                  noteInvalid && 'border-error'
                )}
              />
            </div>
          )}
        </label>
      </div>
    );
  };

  // 단일 선택 시 노트 입력 렌더링
  const renderSingleNote = () => {
    if (!single || !firstSelectedKey) return null;
    const option = options.find((opt) => opt.key === firstSelectedKey);
    if (!option?.requireNote) return null;

    const noteValue = notesForKeys[firstSelectedKey] || '';
    const noteInvalid = markError && noteValue.trim() === '';

    return (
      <div className="flex flex-col">
        <input
          type="text"
          value={noteValue}
          onChange={(e) => handleNoteChange(firstSelectedKey, e.target.value)}
          placeholder={notePlaceholder}
          className={classNames(
            'input w-full border border-black border-opacity-10 rounded-xl',
            noteInvalid && 'border-error'
          )}
        />
      </div>
    );
  };

  return (
    <>
      {/* 바텀시트 모달 */}
      {isOpen && (
        <div
          className={classNames(
            'fixed inset-0 z-50 flex items-end justify-center bg-black bg-opacity-50',
            modalClassName
          )}
          onClick={handleClose}
        >
          <div
            className={classNames(
              'bg-white w-full max-w-lg rounded-t-3xl overflow-hidden',
              'flex flex-col max-h-[80vh]'
            )}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 모달 헤더 */}
            <div className="flex-none flex flex-col gap-2 overflow-hidden pb-4 w-full">
              <div className="flex justify-center">
                <div className="bg-black bg-opacity-10 rounded-full w-10 h-2 mt-3" />
              </div>
              <div className="flex items-center gap-2 px-5">
                <h3 className="flex-1 text-center font-semibold text-lg">{title}</h3>
                <div className="w-0 overflow-visible">
                  <button
                    className="p-2 inline"
                    onClick={handleClose}
                  >
                    <MdClose size={20} className="opacity-60 -top-4 relative right-7" />
                  </button>
                </div>
              </div>
            </div>

            {/* 옵션 목록 */}
            <div className="flex-1 flex flex-col gap-3 overflow-auto px-3 pb-3">
              {options.map(renderOption)}
            </div>

            {/* 닫기 버튼 */}
            <div className="flex-none flex justify-end px-3 py-3 border-t">
              <button
                className="btn"
                onClick={handleClose}
              >
                {closeButtonText}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 메인 UI */}
      <div className={classNames('flex flex-col gap-2', className)}>
        {/* 트리거 입력 필드 */}
        <div className="w-full">
          <div className="form-control w-full">
            <div
              className={classNames(
                'w-full flex cursor-pointer',
                (disabled || readOnly) && 'opacity-50 cursor-not-allowed'
              )}
              onClick={handleOpen}
            >
              <input
                type="text"
                placeholder={placeholder}
                readOnly
                disabled={disabled}
                value={!single && firstSelectedKey ? '' : firstSelectedLabel}
                className={classNames(
                  'flex-1 input pointer-events-none overflow-ellipsis',
                  isRtl ? 'rounded-l-none border-l-0' : 'rounded-r-none border-r-0',
                  hasError && 'border-error',
                  inputClassName
                )}
              />
              <span
                className={classNames(
                  'border-y flex items-center justify-center p-2',
                  isRtl ? 'border-l rounded-l-lg' : 'border-r rounded-r-lg',
                  hasError && 'border-error'
                )}
              >
                <MdChevronLeft className="-rotate-90 opacity-70" size={23} />
              </span>
            </div>
          </div>
        </div>

        {/* 단일 선택 시 노트 입력 */}
        {renderSingleNote()}

        {/* 다중 선택 시 선택된 항목 표시 */}
        {!single && selectedKeys.length > 0 && (
          <div className="flex flex-col gap-1">
            {selectedKeys.map(renderSelectedItem)}
          </div>
        )}

        {/* 에러 메시지 */}
        {hasError && errorMessage && (
          <p className="text-error text-sm mt-1">{errorMessage}</p>
        )}
      </div>
    </>
  );
}

export default memo(V2QsSelectSheet);
